<template>
    <div class="process-dashboard">
        <StekDashboard :entity-id="entityId" entity-type="process" />
        <ProcessInstanceTable
            :instances="processInstances"
            :loading="loadingInstances"
            :compliance-enabled="complianceEnabled"
            class="instances-section"
        />
        <Card v-if="definition && definition.content" class="bpmn-section">
            <template #title>Process Diagram</template>
            <template #content>
                <ProcessDefinition
                    v-model:bpmnArtefact="definition"
                    :isNavigationEnabled="true"
                    :isScrollingEnabled="true"
                    :isMaximizingEnabled="true"
                    :isDisabled="false"
                    class="process-definition-viewer"
                />
            </template>
        </Card>
    </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import Card from "primevue/card";
import { computed, onMounted, ref, toRef, watch } from "vue";

import ProcessDefinition from "@/components/Process/Definition.vue";
import { useStekStore } from "@/composables/stek-manager";
import { useMetricsDashboard } from "@/composables/useMetricsDashboard";
import { Artefact, type BpmnArtefact } from "@/lib/artefacts";
import { type types, useClient, useServices } from "@/lib/openapi";
import { startStekLiveMgtGetProcessInstancesStartPost } from "@/lib/openapi/gen/sdk.gen";
import { followLink } from "@/lib/openapi/helpers";
import ProcessInstanceTable from "@/live/components/ProcessInstanceTable.vue";
import { useReleaseStore } from "@/stores/release-store";

import StekDashboard from "./StekDashboard.vue";

type ProcessInstanceMetrics = types.ProcessInstanceMetrics;

defineOptions({
    name: "ProcessDashboard",
});

const props = defineProps<{
    entityId: string;
    entityType: string;
}>();

const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

const client = useClient({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

// Access the metrics dashboard state for time range and release filter
const { selectedHours: timeRange, selectedRelease } = useMetricsDashboard({
    entityId: toRef(props, "entityId"),
    entityType: toRef(props, "entityType"),
});

const releaseStore = useReleaseStore();
const revisionQuery = computed(() =>
    releaseStore.activeRevision
        ? new URLSearchParams({ revision: releaseStore.activeRevision })
        : undefined
);

const definition = ref<BpmnArtefact | undefined>();
const loadingDefinition = ref(false);
const processInstances = ref<ProcessInstanceMetrics[]>([]);
const loadingInstances = ref(false);

// Compliance tracking
const stekStore = useStekStore();
const { stek } = storeToRefs(stekStore);
const complianceEnabled = computed(
    () => stek.value?.compliance_enabled ?? false
);

async function fetchProcessDefinition() {
    if (!props.entityId) return;

    definition.value = undefined;
    loadingDefinition.value = true;
    try {
        // Fetch the process metadata (no revision — artefact identity is DB-only, not VFS-versioned)
        const processResult =
            await api.startProcessProcessMgtGetProcessStartPost({
                body: { process_id: props.entityId },
            });

        if (
            processResult.response.ok &&
            processResult.data?.process_definition
        ) {
            // Fetch the BPMN file
            const { response, data } = await followLink<File>(
                processResult.data.process_definition,
                "get",
                undefined,
                undefined,
                revisionQuery.value
            );

            if (response.ok && data) {
                // Create BpmnArtefact
                definition.value = await Artefact.createBpmnArtefact({
                    name: "processDefinition",
                    identity: processResult.data.process_definition.identity,
                    blob: data,
                });
            }
        }
    } catch {
        // Failed to fetch process definition, ignore
    } finally {
        loadingDefinition.value = false;
    }
}

async function fetchProcessInstances() {
    if (!props.entityId) return;

    loadingInstances.value = true;
    try {
        const { data } =
            await startStekLiveMgtGetProcessInstancesStartPost<true>({
                client,
                throwOnError: true,
                body: {
                    process_id: props.entityId,
                    hours: timeRange.value,
                    stek_release: selectedRelease.value || undefined,
                },
            });
        processInstances.value = data;
    } catch (error) {
        console.error("Failed to fetch process instances:", error);
        processInstances.value = [];
    } finally {
        loadingInstances.value = false;
    }
}

// Watch for changes in time range or release filter
watch([timeRange, selectedRelease], () => {
    fetchProcessInstances();
});

// Re-fetch definition when release changes
watch(
    () => releaseStore.activeRevision,
    () => {
        fetchProcessDefinition();
    }
);

// Also watch for entityId changes
watch(
    () => props.entityId,
    () => {
        if (props.entityId) {
            fetchProcessDefinition();
            fetchProcessInstances();
        }
    }
);

onMounted(() => {
    fetchProcessDefinition();
    fetchProcessInstances();
});
</script>

<style scoped lang="scss">
.process-dashboard {
    .instances-section {
        margin-top: 2rem;
    }

    .bpmn-section {
        margin-top: 3rem;
    }

    .process-definition-viewer {
        height: 600px;
        max-height: 75vh;
        display: flex;
        padding: 0;
    }
}
</style>
